(() => {
  "use strict";

  // ── Config ────────────────────────────────────────────────────────────────
  const DEBUG = false;
  const log = (...a) => { if (DEBUG) console.debug("[kaching-bundles]", ...a); };

  const KACHING_BUNDLES_KEY = "__kaching_bundles";
  const GIFT_TIER1_ID = 50009138495835;
  const SEED_GIFT_VARIANT_IDS = [50009138495835, 53039572549979];
  const SEED_GIFT_SLOTS = { 50009138495835: "7jPN", 53039572549979: "JrRN" };
  const GIFT_REGISTRY_STORAGE_KEY = "che256_kaching_gift_registry";

  const TIER_B3G2 = "B3G2";
  const TIER_B2G1 = "B2G1";
  const BUNDLE_TITLES_STORAGE_KEY = "che256_kaching_bundle_titles";

  // ── Bundle titles: discovered from the rendered widget ────────────────
  // Bar titles are localised by the store, so they are READ from the widget
  // (.kaching-bundles__bar-title) instead of being hard-coded per language, and
  // cached in localStorage so the cart drawer still recognises them on pages
  // that don't render the widget. A bar only counts as a bundle when it offers
  // gifts — the plain "Buy 1" bar carries no .kaching-bundles__free-gift block
  // and is skipped. The number of gift blocks inside the bar decides the tier
  // (2+ → B3G2, exactly 1 → B2G1); the quantities in the title are the fallback.
  const _tierByTitle = {};   // normalised bar title -> tier

  const normalizeTitle = (title) => String(title || "").replace(/\s+/g, " ").trim();
  const titleKey = (title) => normalizeTitle(title).toLowerCase();
  const getBundleTier = (title) => _tierByTitle[titleKey(title)] || null;

  function registerBundleTitle(title, tier) {
    const key = titleKey(title);
    if (!key || !tier || _tierByTitle[key] === tier) return;
    _tierByTitle[key] = tier;
    try {
      localStorage.setItem(BUNDLE_TITLES_STORAGE_KEY, JSON.stringify(_tierByTitle));
    } catch (_) { /* storage unavailable — keep working from memory */ }
  }

  function loadBundleTitles() {
    try {
      const data = JSON.parse(localStorage.getItem(BUNDLE_TITLES_STORAGE_KEY) || "{}");
      Object.entries(data).forEach(([key, tier]) => {
        if (key && (tier === TIER_B3G2 || tier === TIER_B2G1)) _tierByTitle[key] = tier;
      });
    } catch (_) { /* corrupt/empty — widget discovery refills it */ }
  }

  function tierFromGiftCount(barEl) {
    const count = barEl ? qq(".kaching-bundles__free-gift", barEl).length : 0;
    if (count >= 2) return TIER_B3G2;
    if (count === 1) return TIER_B2G1;
    return null;
  }

  // "Buy 3 Get 2 Free" / "Kup 3, otrzymaj 2 gratis" → 2 gifts → B3G2.
  // "Buy 1" → 0 gifts → not a bundle.
  function tierFromTitleQuantities(title) {
    const nums = normalizeTitle(title).match(/\d+/g);
    if (!nums) return null;
    const giftQty = nums.length > 1 ? Number(nums[1]) : Number(nums[0]) - 1;
    if (giftQty >= 2) return TIER_B3G2;
    if (giftQty === 1) return TIER_B2G1;
    return null;
  }

  function tierForBar(titleEl, title) {
    const byTitle = tierFromTitleQuantities(title);
    // "Buy 1" — and any bar whose title promises no gift — is never a bundle,
    // even if the layout happens to render a gift block inside it.
    if (!byTitle && /\d/.test(title)) return null;
    const bar = titleEl.closest(".kaching-bundles__bar");
    return tierFromGiftCount(bar) || byTitle;
  }

  function scanBundleBars() {
    qq(".kaching-bundles__bar-title").forEach((titleEl) => {
      const title = normalizeTitle(titleEl.textContent);
      if (title) registerBundleTitle(title, tierForBar(titleEl, title));
    });
  }

  // ── Dynamic gift registry ────────────────────────────────────────────────
  // Gift variant ids + their Kaching slot ids are DISCOVERED at runtime from:
  //   • cart lines whose __kaching_bundles carries a `gift` key, and
  //   • the rendered Kaching widget (.kaching-bundles__free-gift).
  // The result is cached in localStorage so the duplicate-add guard already
  // knows the gifts on the next page load (no extra cart fetch needed). The
  // seeds above are only a starting point — discovery always wins.
  const _giftSlotByVariant = {};       // variantId -> Kaching slot id
  const _giftVariantIds = new Set();   // every known gift variant id

  function registerGiftVariant(variantId, slot) {
    const id = Number(variantId);
    if (!id || Number.isNaN(id)) return;
    let changed = false;
    if (!_giftVariantIds.has(id)) { _giftVariantIds.add(id); changed = true; }
    if (slot && _giftSlotByVariant[id] !== slot) { _giftSlotByVariant[id] = slot; changed = true; }
    if (changed) persistGiftRegistry();
  }

  const getGiftVariantIds = () => [..._giftVariantIds];
  const isManagedGiftVariant = (variantId) => _giftVariantIds.has(Number(variantId));

  function persistGiftRegistry() {
    try {
      localStorage.setItem(
        GIFT_REGISTRY_STORAGE_KEY,
        JSON.stringify({ ids: [..._giftVariantIds], slots: _giftSlotByVariant })
      );
    } catch (_) { /* storage unavailable — keep working from memory */ }
  }

  function loadGiftRegistry() {
    // Seeds first (so the guard is armed even before any discovery)…
    SEED_GIFT_VARIANT_IDS.forEach((id) => _giftVariantIds.add(Number(id)));
    Object.entries(SEED_GIFT_SLOTS).forEach(([id, slot]) => {
      _giftSlotByVariant[Number(id)] = slot;
    });
    // …then persisted discovery from a previous visit (overrides seeds).
    try {
      const data = JSON.parse(localStorage.getItem(GIFT_REGISTRY_STORAGE_KEY) || "{}");
      (data.ids || []).forEach((id) => _giftVariantIds.add(Number(id)));
      Object.entries(data.slots || {}).forEach(([id, slot]) => {
        if (slot) _giftSlotByVariant[Number(id)] = slot;
      });
    } catch (_) { /* corrupt/empty — seeds already applied */ }
  }

  // Settle (cart-stabilisation) tuning
  const SETTLE_DEBOUNCE_MS = 400;
  const SETTLE_INITIAL_DELAY = 250;
  const SETTLE_STABLE_READS = 2;
  const SETTLE_TIER_ABSENT_READS = 2;
  const SETTLE_READ_INTERVAL = 200;
  const SETTLE_MAX_READS = 12;

  // The ONLY controls allowed to trigger a gift re-sync.
  const SYNC_TRIGGER_SELECTOR = [
    ".amp-cart__line-quantity-selector-more",
    ".amp-cart__line-quantity-selector-less",
    ".amp-cart__cart-line-remove"
  ].join(",");

  // CSS hooks styled in kaching-bundles.css
  const ZERO_CLASS = "kaching-bundles__gift-zero";
  const ZERO_WRAP_CLASS = "kaching-bundles__gift-price-wrap";

  // ── Tiny utils ────────────────────────────────────────────────────────────
  const q = (s, o) => (o ? o.querySelector(s) : document.querySelector(s));
  const qq = (s, o) => [...(o || document).querySelectorAll(s)];
  const wait = (ms) => new Promise((r) => setTimeout(r, ms));

  function debounce(fn, ms) {
    let t;
    return function (...args) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), ms);
    };
  }

  function observe(target, callback) {
    const observer = new MutationObserver(callback);
    observer.observe(target, { childList: true, subtree: true });
    return observer;
  }

  // ── Cart-item helpers ─────────────────────────────────────────────────────
  function normalizeItemProperties(item) {
    const properties = item?.properties;
    if (!properties) return null;
    if (typeof properties === "object" && !Array.isArray(properties)) return properties;
    if (Array.isArray(properties)) {
      return Object.fromEntries(properties.filter((p) => p?.name).map((p) => [p.name, p.value]));
    }
    return null;
  }

  function parseKachingBundlesRaw(item) {
    const raw = normalizeItemProperties(item)?.[KACHING_BUNDLES_KEY];
    if (!raw) return null;
    try {
      return typeof raw === "string" ? JSON.parse(raw) : raw;
    } catch (_) {
      return null;
    }
  }

  const isKachingGiftLine = (item) => Boolean(parseKachingBundlesRaw(item)?.gift);

  function getKachingGiftLines(cart, variantId = null) {
    return cart.items.filter((item) => {
      if (!isManagedGiftVariant(item.variant_id)) return false;
      if (variantId != null && item.variant_id !== variantId) return false;
      return isKachingGiftLine(item);
    });
  }

  // ── Cart API (un-patched fetch, so it never re-enters our guard) ──────────
  async function fetchCart() {
    const res = await window._originalFetch("/cart.js");
    return res.json();
  }

  async function getExistingCartItems() {
    try {
      const cart = await fetchCart();
      const found = {};
      cart.items.forEach((item) => {
        if (isKachingGiftLine(item)) found[item.variant_id] = item;
      });
      return found;
    } catch (e) {
      log("Cart check failed", e);
      return {};
    }
  }

  // ── Deal context cache ───────────────────────────────────────────────────
  let _dealContextCache = null;

  function cacheKachingGiftMeta(item) {
    const parsed = parseKachingBundlesRaw(item);
    if (!parsed) return;
    if (parsed.deal) {
      // Prefer a context that carries `v` (the deal-instance id) — Kaching binds
      // every line to the deal via `v`, and a re-added gift MUST carry it too.
      if (parsed.v || !_dealContextCache?.v) {
        _dealContextCache = {
          id: parsed.id,
          deal: parsed.deal,
          v: parsed.v || _dealContextCache?.v,
          ab: parsed.ab
        };
      }
    }
    // Any line carrying a `gift` key IS a gift — discover its id + slot.
    if (parsed.gift) registerGiftVariant(item.variant_id, parsed.gift);
  }

  const cacheKachingGiftMetaFromAddItem = (item) =>
    cacheKachingGiftMeta({ variant_id: Number(item.id), properties: item.properties });

  const rememberCartGiftMeta = (cart) => cart.items.forEach(cacheKachingGiftMeta);

  function getDealContextFromCart(cart) {
    // Prefer a line that carries `v` (the main product line always does). Never
    // derive the deal context from a gift line we may have added ourselves.
    let fallback = null;
    for (const item of cart.items) {
      const parsed = parseKachingBundlesRaw(item);
      if (!parsed?.deal) continue;
      if (parsed.v) return { id: parsed.id, deal: parsed.deal, v: parsed.v, ab: parsed.ab };
      fallback = fallback || { id: parsed.id, deal: parsed.deal, ab: parsed.ab };
    }
    return fallback || _dealContextCache;
  }

  function getGiftSlotId(variantId) {
    const id = Number(variantId);
    if (_giftSlotByVariant[id]) return _giftSlotByVariant[id];
    const link = q(`.kaching-bundles__free-gift__link[href*="variant=${id}"]`);
    const slot = link?.closest(".kaching-bundles__free-gift")?.dataset?.freeGiftId;
    if (slot) {
      registerGiftVariant(id, slot);
      return slot;
    }
    return SEED_GIFT_SLOTS[id] || null;
  }

  function buildGiftAddPayload(variantId, dealContext, giftSlotId) {
    // Mirror Kaching's OWN gift-line shape exactly, in the same key order, so the
    // line is byte-identical to a native gift and Kaching keeps the deal (and its
    // "Buy 3 Get 2 Free" title) intact instead of re-deriving it:
    //   {"id":"…","deal":"HM3F","v":"…","gift":"…"}
    const bundle = { id: dealContext.id, deal: dealContext.deal };
    if (dealContext.v) bundle.v = dealContext.v;
    if (dealContext.ab) bundle.ab = dealContext.ab; // only if the deal actually uses it
    bundle.gift = giftSlotId;
    return {
      id: variantId,
      quantity: 1,
      properties: { [KACHING_BUNDLES_KEY]: JSON.stringify(bundle) }
    };
  }

  // ── Duplicate-add guard (passive — never schedules a re-sync) ─────────────
  function filterBlockedVariants(parsedBody) {
    const items = parsedBody.items;
    if (!Array.isArray(items)) return { filtered: parsedBody, removedItems: [] };
    const isBlockedGiftAdd = (item) =>
      isKachingGiftLine(item) && isManagedGiftVariant(Number(item.id));
    const removedItems = items.filter(isBlockedGiftAdd);
    const remaining = items.filter((item) => !isBlockedGiftAdd(item));
    return { filtered: { ...parsedBody, items: remaining }, removedItems };
  }

  async function buildInterceptedBody(bodyText) {
    // Quick bail-out: only parse when the body could touch a known gift.
    if (!bodyText.includes(KACHING_BUNDLES_KEY) &&
        !getGiftVariantIds().some((id) => bodyText.includes(String(id)))) {
      return { shouldIntercept: false };
    }

    let parsed;
    try {
      parsed = JSON.parse(bodyText);
    } catch (_) {
      return { shouldIntercept: false };
    }

    // Register any gifts in this add BEFORE filtering, so newly-seen gifts are
    // recognised by the duplicate-add guard on this very request.
    (parsed.items || []).forEach(cacheKachingGiftMetaFromAddItem);
    const { filtered, removedItems } = filterBlockedVariants(parsed);
    if (!removedItems.length) return { shouldIntercept: false };

    const existingCartItems = await getExistingCartItems();
    const alreadyInCart = removedItems.filter((i) => existingCartItems[Number(i.id)]);
    if (!alreadyInCart.length) return { shouldIntercept: false }; // first add — allow

    const notYetInCart = removedItems.filter((i) => !existingCartItems[Number(i.id)]);
    if (notYetInCart.length) filtered.items = [...filtered.items, ...notYetInCart];

    const fakeItems = alreadyInCart.map((i) => existingCartItems[Number(i.id)]);
    log("Blocked duplicate gift add:", alreadyInCart.map((i) => i.id));
    return {
      shouldIntercept: true,
      isOnlyItems: filtered.items.length === 0,
      newBody: JSON.stringify(filtered),
      fakeItems
    };
  }

  // ── Tier detection ───────────────────────────────────────────────────────
  function getCartLineBundleTiers(cart) {
    // A discount only counts when its title was discovered on a bundle bar, so
    // unrelated cart discounts can never be mistaken for a bundle.
    const found = new Set();
    cart.items.forEach((item) => {
      (item.discounts || []).forEach((d) => {
        const tier = getBundleTier(d.title);
        if (tier) found.add(tier);
      });
    });
    return found;
  }

  function getActiveBundleTier(cart) {
    const tiers = getCartLineBundleTiers(cart);
    if (tiers.has(TIER_B3G2)) return TIER_B3G2;   // highest tier wins
    if (tiers.has(TIER_B2G1)) return TIER_B2G1;
    return null;
  }

  function getAllowedGiftIds(cart) {
    const tier = getActiveBundleTier(cart);
    if (tier === TIER_B3G2) return getGiftVariantIds();   // every known gift
    if (tier === TIER_B2G1) return [GIFT_TIER1_ID];        // tier-1 only
    return [];
  }

  // ── Gift-sync computation ────────────────────────────────────────────────
  function computeMissingGiftAdds(cart) {
    const tier = getActiveBundleTier(cart);
    if (!tier) return [];
    rememberCartGiftMeta(cart);
    const dealContext = getDealContextFromCart(cart);
    if (!dealContext?.deal) {
      log("Cannot re-add gifts — no Kaching deal context in cart");
      return [];
    }
    const itemsToAdd = [];
    getAllowedGiftIds(cart).forEach((variantId) => {
      if (getKachingGiftLines(cart, variantId).length) return;
      const giftSlot = getGiftSlotId(variantId);
      if (!giftSlot) {
        log("Cannot re-add gift — missing slot id for variant", variantId);
        return;
      }
      itemsToAdd.push(buildGiftAddPayload(variantId, dealContext, giftSlot));
    });
    return itemsToAdd;
  }

  async function addMissingGifts(itemsToAdd) {
    if (!itemsToAdd.length) return;
    try {
      const res = await window._originalFetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: itemsToAdd })
      });
      if (res.ok) log("Re-added missing gifts:", itemsToAdd);
      else log("Gift re-add failed", res.status, await res.text().catch(() => ""));
    } catch (e) {
      log("Gift re-add error", e);
    }
  }

  function computeGiftUpdates(cart, { removeWhenNoTier = false } = {}) {
    const tier = getActiveBundleTier(cart);
    const allowed = getAllowedGiftIds(cart);
    const updates = {};
    getGiftVariantIds().forEach((variantId) => {
      const giftLines = getKachingGiftLines(cart, variantId);
      if (!giftLines.length) return;
      if (!allowed.includes(variantId)) {
        if (tier || removeWhenNoTier) giftLines.forEach((l) => (updates[l.key] = 0));
        return;
      }
      const [keep, ...extras] = giftLines;
      if (keep.quantity !== 1) updates[keep.key] = 1;
      extras.forEach((l) => (updates[l.key] = 0));
    });
    return updates;
  }

  const cartSignature = (cart) =>
    [
      cart.item_count,
      getActiveBundleTier(cart) || "NONE",
      ...cart.items.map((i) => `${i.variant_id}:${i.quantity}`)
    ].join("|");

  // ── Apply + settle ───────────────────────────────────────────────────────
  // Only notify the theme/drawer to re-render. We deliberately DO NOT fire a
  // no-op /cart/update.js: an empty update makes Shopify re-process the WHOLE
  // cart and merge duplicate main-product lines, which forces Kaching to
  // recompute and drop the "Buy 3 Get 2 Free" / "Buy 2 Get 1 Free" title a few
  // seconds later. This script must only ever touch the gift lines.
  function refreshCartUI() {
    document.dispatchEvent(new CustomEvent("cart:refresh"));
    document.dispatchEvent(new CustomEvent("cart:updated"));
  }

  async function applyGiftCartSync(cart, updates, itemsToAdd) {
    if (Object.keys(updates).length) {
      const res = await window._originalFetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ updates })
      });
      if (res.ok) log("Gift lines synced:", updates);
      else log("Gift sync failed", res.status, await res.text().catch(() => ""));
    }

    let cartForAdds = cart;
    if (Object.keys(updates).length) {
      cartForAdds = await fetchCart();
      rememberCartGiftMeta(cartForAdds);
    }
    const missingAdds = itemsToAdd.length ? itemsToAdd : computeMissingGiftAdds(cartForAdds);
    await addMissingGifts(missingAdds);
    refreshCartUI();
  }

  async function verifyAndSyncGifts() {
    await wait(SETTLE_INITIAL_DELAY);

    let prevSig = null;
    let stableReads = 0;
    let tierAbsentReads = 0;
    let settledCart = null;

    // Wait until the cart signature (which INCLUDES the active bundle tier) is
    // stable across consecutive reads. This is essential: after a qty change
    // Kaching recomputes the discount title asynchronously (e.g. B3G2 → B2G1),
    // so we must not decide on a stale tier — otherwise the now-disallowed gift
    // never gets removed.
    for (let read = 1; read <= SETTLE_MAX_READS; read++) {
      const cart = await fetchCart();
      rememberCartGiftMeta(cart);
      const tier = getActiveBundleTier(cart);

      const sig = cartSignature(cart);
      if (sig === prevSig) stableReads++;
      else {
        stableReads = 1;
        prevSig = sig;
      }
      if (!tier) tierAbsentReads++;
      else tierAbsentReads = 0;

      if (stableReads >= (tier ? SETTLE_STABLE_READS : SETTLE_TIER_ABSENT_READS)) {
        settledCart = cart;
        break;
      }
      await wait(SETTLE_READ_INTERVAL);
    }

    if (!settledCart) {
      log("Cart never stabilized — skipping gift sync");
      return;
    }

    rememberCartGiftMeta(settledCart);
    const tier = getActiveBundleTier(settledCart);
    const removeWhenNoTier = !tier && tierAbsentReads >= SETTLE_TIER_ABSENT_READS;
    const updates = computeGiftUpdates(settledCart, { removeWhenNoTier });
    const itemsToAdd = tier ? computeMissingGiftAdds(settledCart) : [];

    if (!Object.keys(updates).length && !itemsToAdd.length) {
      log("Gifts already correct at settle");
      return;
    }
    if (!tier && !removeWhenNoTier) {
      log("No bundle title yet — leaving cart untouched");
      return;
    }

    log(`Settled — tier "${tier || "none"}", syncing:`, { updates, itemsToAdd });
    await applyGiftCartSync(settledCart, updates, itemsToAdd);
  }

  // Single-flight: one settle cycle at a time, with a single trailing re-run.
  let _settleRunning = false;
  let _settleQueued = false;

  async function runGiftSettleCycle() {
    if (_settleRunning) {
      _settleQueued = true;
      return;
    }
    _settleRunning = true;
    try {
      do {
        _settleQueued = false;
        await verifyAndSyncGifts();
      } while (_settleQueued);
    } catch (e) {
      log("settle cycle error", e);
    } finally {
      _settleRunning = false;
    }
  }

  const scheduleGiftSettleCheck = debounce(runGiftSettleCycle, SETTLE_DEBOUNCE_MS);

  // ── fetch / XHR patch — DUPLICATE GUARD ONLY (no re-sync scheduling) ──────
  function patchFetch() {
    window._originalFetch = window.fetch.bind(window);
    window.fetch = async function (input, init = {}) {
      const url = typeof input === "string" ? input : input?.url || "";
      if (url.includes("/cart/add")) {
        try {
          let bodyText = "";
          if (init.body) {
            bodyText = typeof init.body === "string" ? init.body : await new Response(init.body).text();
          }
          const intercept = await buildInterceptedBody(bodyText);
          if (intercept.shouldIntercept) {
            if (intercept.isOnlyItems) {
              return new Response(JSON.stringify({ items: intercept.fakeItems }), {
                status: 200,
                headers: { "Content-Type": "application/json" }
              });
            }
            return window._originalFetch(input, { ...init, body: intercept.newBody });
          }
        } catch (e) {
          log("Fetch intercept error", e);
        }
      }
      return window._originalFetch(input, init);
    };
  }

  function patchXHR() {
    const OriginalXHR = window.XMLHttpRequest;
    window.XMLHttpRequest = function () {
      const xhr = new OriginalXHR();
      const origOpen = xhr.open.bind(xhr);
      const origSend = xhr.send.bind(xhr);
      let _url = "";

      xhr.open = function (method, url, ...rest) {
        _url = url || "";
        return origOpen(method, url, ...rest);
      };

      xhr.send = async function (body) {
        if (_url.includes("/cart/add")) {
          try {
            const intercept = await buildInterceptedBody(body ? String(body) : "");
            if (intercept.shouldIntercept) {
              if (intercept.isOnlyItems) {
                Object.defineProperty(xhr, "readyState", { get: () => 4, configurable: true });
                Object.defineProperty(xhr, "status", { get: () => 200, configurable: true });
                Object.defineProperty(xhr, "responseText", {
                  get: () => JSON.stringify({ items: intercept.fakeItems }),
                  configurable: true
                });
                xhr.dispatchEvent(new Event("load"));
                return;
              }
              return origSend(intercept.newBody);
            }
          } catch (e) {
            log("XHR intercept error", e);
          }
        }
        return origSend(body);
      };

      return xhr;
    };
  }

  // ── "0.00" label next to the gift's crossed-out full price ────────────────
  function getPriceCurrency(text) {
    const t = text.trim();
    if (/€|\bEUR\b/i.test(t)) return "EUR";
    if (/\$|\bUSD\b/i.test(t)) return "USD";
    return null;
  }

  function buildZeroPrice(priceText) {
    const text = priceText.trim();
    const symbol = text.match(/^[^\d]+/)?.[0] || "";
    const decimalSep = /,\d{2}$/.test(text) ? "," : ".";
    return `${symbol}0${decimalSep}00`;
  }

  function processFreeGiftBlock(freeGiftEl) {
    // Discover this gift's variant id + slot straight from the widget markup.
    const slot = freeGiftEl.dataset?.freeGiftId;
    const href = q(".kaching-bundles__free-gift__link", freeGiftEl)?.getAttribute("href");
    const variantMatch = href?.match(/variant=(\d+)/);
    if (variantMatch) registerGiftVariant(variantMatch[1], slot);

    const textEl = q(".kaching-bundles__free-gift__text", freeGiftEl);
    const priceEl = q(".kaching-bundles__free-gift__full-price", freeGiftEl);
    if (!priceEl) return;

    // Keep the gift-text currency label in sync with the actual price currency.
    if (textEl && /\bEUR\b/i.test(textEl.textContent) &&
        getPriceCurrency(priceEl.textContent) === "USD") {
      textEl.textContent = textEl.textContent.replace(/\bEUR\b/g, "USD");
    }

    if (priceEl.previousElementSibling?.classList.contains(ZERO_CLASS)) return;
    const wrapper = document.createElement("div");
    wrapper.className = ZERO_WRAP_CLASS;
    wrapper.innerHTML = `<div class="${ZERO_CLASS}">${buildZeroPrice(priceEl.textContent)}</div>`;
    priceEl.replaceWith(wrapper);
    wrapper.appendChild(priceEl);
  }

  function watchKachingWidget() {
    const run = () => {
      scanBundleBars();
      qq(".kaching-bundles__free-gift").forEach(processFreeGiftBlock);
    };
    run();
    observe(document.body, run);
  }

  // ── Cart-drawer controls: the ONLY thing that triggers a gift re-sync ─────
  function bindCartControls() {
    // Capture phase so a cart-drawer app that calls stopPropagation() in the
    // bubbling phase can't prevent us from seeing the click.
    document.addEventListener(
      "click",
      (event) => {
        const trigger = event.target.closest?.(SYNC_TRIGGER_SELECTOR);
        if (trigger) {
          log("Cart control clicked:", trigger.className || trigger.tagName);
          scheduleGiftSettleCheck();
        }
      },
      true
    );
  }

  // ── Boot ──────────────────────────────────────────────────────────────────
  loadGiftRegistry();  // seed + restore discovered gifts before anything runs
  loadBundleTitles();  // restore bundle bar titles discovered on earlier visits
  patchFetch();
  patchXHR();
  bindCartControls();
  watchKachingWidget();
})();
